import { useEffect, useState } from 'react';
import { MAX_TITLE_LENGTH } from '../../constants';
import { getRoomWorkflowTone } from '../../lib/roomWorkflow';

export default function ChatListModal({
  isChatListModalOpen,
  setIsChatListModalOpen,
  chatRooms,
  activeRoomId,
  onSelectRoom,
  onDeleteRoom,
  onUpdateRoomTitle,
  onStartNewRoom,
  statusFilter = null,
  onClearStatusFilter,
}) {
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [tempTitle, setTempTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRooms = chatRooms.filter(room => {
    const matchesTitle = (room.title || 'Untitled Chat').toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesTitle) return false;
    if (statusFilter === 'pending_summary') {
      return room.workflow?.requiresMemo ?? ((room.status === 'active') || (!room.status && !room.summaryNoteId));
    }
    return true;
  });

  useEffect(() => {
    if (isChatListModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isChatListModalOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsChatListModalOpen(false);
      }
    };
    if (isChatListModalOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isChatListModalOpen, setIsChatListModalOpen]);


  const handleStartEdit = (e, room) => {
    e.stopPropagation();
    setEditingRoomId(room.id);
    setTempTitle(room.title || '');
  };

  const handleSaveEdit = async (e, roomId) => {
    e.stopPropagation();
    if (tempTitle.trim() && onUpdateRoomTitle) {
      await onUpdateRoomTitle(roomId, tempTitle.trim());
    }
    setEditingRoomId(null);
    setTempTitle('');
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingRoomId(null);
    setTempTitle('');
  };

  if (!isChatListModalOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[160] bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) setIsChatListModalOpen(false);
      }}
    >
      <div className="w-full max-w-md bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden flex flex-col max-h-[85vh]">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center bg-gray-800/90 backdrop-blur-sm sticky top-0 z-10">
          <h3 className="text-base font-bold text-white">過去のチャット</h3>
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                onStartNewRoom?.();
                setIsChatListModalOpen(false);
              }}
              className="text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
            >
              <span>+ 新規ルーム</span>
            </button>
            <button aria-label="閉じる" type="button" onClick={() => setIsChatListModalOpen(false)} className="text-gray-400 hover:text-white text-xl leading-none p-1">
              ×
            </button>
          </div>
        </div>

        <div className="p-3 border-b border-gray-700 bg-gray-900/30">
          <div className="relative">
            <input
              type="text"
              placeholder="タイトルで検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 text-white text-sm rounded-lg pl-9 pr-3 py-2 outline-none focus:border-indigo-500 transition-all"
            />
            <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {statusFilter === 'pending_summary' && (
          <div className="px-3 py-2 bg-blue-950/30 border-b border-blue-800/40 flex items-center justify-between gap-2">
            <span className="text-[11px] font-bold text-blue-300">まとめ待ちのルームのみ表示</span>
            <button
              onClick={onClearStatusFilter}
              className="text-[10px] text-blue-400 hover:text-blue-200 flex items-center gap-1"
              aria-label="フィルタを解除"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              解除
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto overscroll-contain p-2 space-y-2 min-h-0">
          {filteredRooms.length === 0 && (
            <div className="text-center text-sm text-gray-500 py-12">
              {searchQuery ? '一致するチャットが見つかりませんでした。' : statusFilter ? 'まとめ待ちのルームはありません。' : '履歴はありません。'}
            </div>
          )}
          {filteredRooms.map(room => (
            <div key={room.id} className="relative group">
              {editingRoomId === room.id ? (
                <div className="p-3 bg-gray-700/50 rounded flex items-center gap-2">
                  <input
                    autoFocus
                    type="text"
                    value={tempTitle}
                    onChange={(e) => setTempTitle(e.target.value)}
                    maxLength={MAX_TITLE_LENGTH}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit(e, room.id);
                      if (e.key === 'Escape') handleCancelEdit(e);
                    }}
                    className="flex-1 bg-gray-800 border border-indigo-500 text-white px-2 py-1 rounded text-sm focus:outline-none"
                  />
                  <button
                    onClick={(e) => handleSaveEdit(e, room.id)}
                    className="text-green-400 hover:text-green-300 p-1"
                    title="保存"
                    aria-label="保存"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="text-gray-400 hover:text-gray-300 p-1"
                    title="キャンセル"
                    aria-label="キャンセル"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                  </button>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => { onSelectRoom(room.id); setIsChatListModalOpen(false); }}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      activeRoomId === room.id
                        ? 'bg-indigo-600/20 border border-indigo-500/50 text-white'
                        : 'bg-gray-700/30 border border-transparent hover:bg-gray-700/50 text-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-start gap-3 mb-1.5 pr-14">
                      <p className={`text-sm font-bold truncate flex-1 ${activeRoomId === room.id ? 'text-indigo-200' : ''}`}>
                        {room.title || 'Untitled Chat'}
                      </p>
                      <span className="text-[10px] opacity-40 shrink-0 tabular-nums">
                        {room.updatedAt?.seconds
                          ? new Date(room.updatedAt.seconds * 1000).toLocaleString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : room.updatedAt && typeof room.updatedAt === 'string'
                            ? new Date(room.updatedAt).toLocaleString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                            : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold tracking-tight ${getRoomWorkflowTone(room.workflow?.key).badgeClass}`}>
                        {room.workflow?.shortLabel || room.workflow?.label || room.status || (room.summaryNoteId ? 'summarized' : 'active')}
                      </span>
                      <span className="text-[9px] text-gray-500 font-bold uppercase tracking-tighter bg-gray-900/50 px-1 rounded">
                        {room.mode || 'input'}
                      </span>
                    </div>
                    {room.workflow?.timingHint && (
                      <p className="mt-2 text-[10px] leading-relaxed text-gray-500 line-clamp-2">
                        {room.workflow.timingHint}
                      </p>
                    )}
                  </button>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                    <button
                      onClick={(e) => handleStartEdit(e, room)}
                      className="text-gray-400 hover:text-blue-400 p-1"
                      title="名前変更"
                      aria-label="名前変更"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                    </button>
                    {onDeleteRoom && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteRoom(room.id);
                        }}
                        className="text-gray-400 hover:text-red-400 transition-colors p-1"
                        title="削除"
                        aria-label="削除"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
